import React, { useState } from 'react';
import { X, Mail, User, Building, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import './ContactModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    // Client-side validation (server also validates)
    if (formData.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.message.trim().length < 10) {
      setError('Message must be at least 10 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate before submitting
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send to secure backend endpoint
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim() || null,
          message: formData.message.trim()
        })
      });

      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', company: '', message: '' });
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Error sending message:', err);
      setIsSubmitting(false);
      setError(err.message || 'Failed to send message. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        {/* Header */}
        <div className="modal-header">
          <Mail className="modal-icon" size={48} />
          <h2 className="modal-title">Get in Touch</h2>
          <p className="modal-subtitle">
            Building something audacious? Tell us about it.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {/* Form or Success State */}
        {!isSubmitted ? (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                <User size={18} />
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                minLength={2}
                maxLength={100}
                className="form-input"
                placeholder="Your full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <Mail size={18} />
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                maxLength={254}
                className="form-input"
                placeholder="your@email.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="company" className="form-label">
                <Building size={18} />
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                maxLength={200}
                className="form-input"
                placeholder="Your company or startup"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                <MessageSquare size={18} />
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                minLength={10}
                maxLength={5000}
                className="form-textarea"
                placeholder="Tell us what you're building—or what you need built. An app, a platform, an AI-powered tool, a wild idea. We're here for all of it."
                rows="5"
              />
            </div>

            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Message
                </>
              )}
            </button>
          </form>
        ) : (
          <div className="success-state">
            <CheckCircle className="success-icon" size={80} />
            <h3 className="success-title">Message Sent!</h3>
            <p className="success-text">
              We&apos;ve received your message and will get back to you within 24-48 hours.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="modal-footer">
          <p>Based on Earth - For Now | Specifically New Mexico, USA</p>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
